import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import ExploreIcon from '@mui/icons-material/ExploreOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsOutlined';
import GavelIcon from '@mui/icons-material/GavelOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CircularProgress from '@mui/material/CircularProgress';

import {
  AdminTaskRow,
  adminTaskApi,
  PublishTaskEntityResult,
  PublishTasksResponse,
} from '../../../../api/adminTaskApi';
import {
  AdminAdventureRow,
  adminAdventureApi,
  PublishAdventureEntityResult,
  PublishAdventuresResponse,
} from '../../../../api/adminAdventureApi';
import {
  AdminCohortRow,
  adminCohortApi,
  PublishCohortEntityResult,
  PublishCohortsResponse,
} from '../../../../api/adminCohortApi';
import {
  AdminSweepstakesRow,
  adminSweepstakesApi,
  PublishSweepstakesEntityResult,
  PublishSweepstakesResponse,
} from '../../../../api/adminSweepstakesApi';
import {
  AdminToSRow,
  adminToSApi,
  PublishToSEntityResult,
  PublishToSResponse,
} from '../../../../api/adminToSApi';
import {
  AdminQuestionnaireRow,
  adminQuestionnaireApi,
  PublishQuestionnaireEntityResult,
  PublishQuestionnairesResponse,
} from '../../../../api/adminQuestionnaireApi';
import {
  AdminCollectionRow,
  adminCollectionApi,
  PublishCollectionEntityResult,
  PublishCollectionsResponse,
} from '../../../../api/adminCollectionApi';
import {
  AdminPlanCohortPurseMappingRow,
  adminPlanCohortPurseMappingApi,
  PublishMappingEntityResult,
  PublishMappingsResponse,
} from '../../../../api/adminPlanCohortPurseMappingApi';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * Kind of entity being published. Drives which client (tasks vs.
 * adventures vs. cohorts vs. sweepstakes vs. ToS) handles the HTTP call,
 * the dialog header copy, and the row icon. Adding a new kind is a
 * one-place extension point.
 */
export type PublishEntityKind =
  | 'task'
  | 'adventure'
  | 'cohort'
  | 'sweepstakes'
  | 'terms_of_service'
  | 'questionnaire'
  | 'task_reward_collection'
  | 'plan_cohort_purse_mapping';

/**
 * A single entity queued for publish. `baseline_version` is the version
 * that was last published (or null if never published); `current_version`
 * is the local/current draft we're about to push out. `code` replaces
 * the R3 `task_code` field so the dialog can handle any entity. For
 * back-compat, `task_code` is still accepted on input and mirrored into
 * `code` by the adapter.
 */
export interface PublishEntity {
  /** Entity kind — `'task'` for back-compat when absent. */
  entity?: PublishEntityKind;
  /** Primary key (task_code / adventure_code). */
  code: string;
  /**
   * Legacy alias — older callers populate `task_code` instead of `code`.
   * Keep both in sync; the dialog reads from `code` with `task_code`
   * as the fallback so existing TasksList + TaskEdit continue to work
   * without simultaneous edits.
   */
  task_code?: string;
  /** Human-readable name used in the row subtitle. */
  task_name?: string | null;
  name?: string | null;
  current_version: number;
  baseline_version: number | null;
  /** Field names that changed vs baseline (drives the line-2 chips). */
  changed_fields?: string[];
  /** Per-field old/new values for the Collapse diff view. */
  diff?: Array<{ field: string; old: unknown; new: unknown }>;
}

/**
 * Normalized result wire shape the dialog consumes. Both `adminTaskApi`
 * and `adminAdventureApi` publish shapes converge here so the retry /
 * per-row UI logic stays entity-agnostic.
 */
export interface PublishBatchResult {
  batch_id: string;
  results: Array<{
    code: string;
    ok: boolean;
    published_version?: number;
    error?: string;
    message?: string;
    current_version?: number;
  }>;
}

export interface PublishConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Entity kind (task | adventure). Optional; falls back to whatever
   * each entity row specifies, then to 'task' for the legacy caller
   * shape. Required when `entities` is empty-but-non-task (unlikely).
   */
  entity?: PublishEntityKind;
  entities: PublishEntity[];
  /** Called after the dialog closes so the caller can refetch + snackbar. */
  onCompleted: (response: PublishBatchResult | null) => void;
}

type DialogPhase = 'review' | 'publishing' | 'result';

type RowStatus = 'queued' | 'publishing' | 'published' | 'failed';

interface RowRuntime {
  status: RowStatus;
  published_version?: number;
  error?: string;
  message?: string;
}

/* ------------------------------------------------------------------ */
/*  Local helpers                                                      */
/* ------------------------------------------------------------------ */

function renderVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Resolve `entity.code` with legacy `task_code` fallback. */
function codeOf(e: PublishEntity): string {
  return e.code || e.task_code || '';
}

function nameOf(e: PublishEntity): string | null {
  return e.name ?? e.task_name ?? null;
}

function kindOf(entity: PublishEntityKind | undefined, e: PublishEntity): PublishEntityKind {
  return entity ?? e.entity ?? 'task';
}

/**
 * Mint a batch id. Uses `crypto.randomUUID()` when available (all modern
 * browsers + jsdom), falls back to a cheap random when not (the only
 * realistic case is a very old browser — the server re-mints on arrival
 * so a weak client id is only a cosmetic issue).
 */
function mintBatchId(): string {
  const c = (typeof crypto !== 'undefined' ? crypto : undefined) as
    | (Crypto & { randomUUID?: () => string })
    | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `r4-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Dispatch a publish call for a given entity kind. Normalizes the task
 * and adventure response shapes into `PublishBatchResult` so the caller
 * doesn't branch on wire-format.
 */
async function dispatchPublish(
  kind: PublishEntityKind,
  subset: PublishEntity[],
  batchId: string,
): Promise<PublishBatchResult> {
  if (kind === 'adventure') {
    const res: PublishAdventuresResponse = await adminAdventureApi.publish({
      entities: subset.map((e) => ({
        adventure_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishAdventureEntityResult) => ({
        code: r.adventure_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'cohort') {
    const res: PublishCohortsResponse = await adminCohortApi.publish({
      entities: subset.map((e) => ({
        cohort_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishCohortEntityResult) => ({
        code: r.cohort_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'sweepstakes') {
    const res: PublishSweepstakesResponse = await adminSweepstakesApi.publish({
      entities: subset.map((e) => ({
        sweepstakes_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishSweepstakesEntityResult) => ({
        code: r.sweepstakes_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'terms_of_service') {
    const res: PublishToSResponse = await adminToSApi.publish({
      entities: subset.map((e) => ({
        tos_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishToSEntityResult) => ({
        code: r.tos_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'questionnaire') {
    const res: PublishQuestionnairesResponse = await adminQuestionnaireApi.publish({
      entities: subset.map((e) => ({
        questionnaire_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishQuestionnaireEntityResult) => ({
        code: r.questionnaire_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'task_reward_collection') {
    const res: PublishCollectionsResponse = await adminCollectionApi.publish({
      entities: subset.map((e) => ({
        collection_code: codeOf(e),
        base_version: e.current_version,
      })),
      batch_id: batchId,
    });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishCollectionEntityResult) => ({
        code: r.collection_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  if (kind === 'plan_cohort_purse_mapping') {
    const res: PublishMappingsResponse =
      await adminPlanCohortPurseMappingApi.publish({
        entities: subset.map((e) => ({
          mapping_code: codeOf(e),
          base_version: e.current_version,
        })),
        batch_id: batchId,
      });
    return {
      batch_id: res.batch_id,
      results: res.results.map((r: PublishMappingEntityResult) => ({
        code: r.mapping_code,
        ok: r.ok,
        published_version: r.published_version,
        error: r.error,
        message: r.message,
        current_version: r.current_version,
      })),
    };
  }
  // default: task
  const res: PublishTasksResponse = await adminTaskApi.publish({
    entities: subset.map((e) => ({
      task_code: codeOf(e),
      base_version: e.current_version,
    })),
    batch_id: batchId,
  });
  return {
    batch_id: res.batch_id,
    results: res.results.map((r: PublishTaskEntityResult) => ({
      code: r.task_code,
      ok: r.ok,
      published_version: r.published_version,
      error: r.error,
      message: r.message,
      current_version: r.current_version,
    })),
  };
}

/** Map entity kind → icon for the ReviewRow. */
function iconForKind(kind: PublishEntityKind): React.ComponentType<{ sx?: any }> {
  if (kind === 'adventure') return ExploreIcon;
  if (kind === 'cohort') return GroupsIcon;
  if (kind === 'sweepstakes') return EmojiEventsIcon;
  if (kind === 'terms_of_service') return GavelIcon;
  if (kind === 'questionnaire') return QuizIcon;
  if (kind === 'task_reward_collection') return CollectionsBookmarkIcon;
  if (kind === 'plan_cohort_purse_mapping') return AccountTreeIcon;
  return AssignmentIcon;
}

/** Map entity kind → noun for header copy. */
function nounForKind(kind: PublishEntityKind): { one: string; many: string } {
  switch (kind) {
    case 'adventure':
      return { one: 'adventure', many: 'adventures' };
    case 'cohort':
      return { one: 'cohort', many: 'cohorts' };
    case 'sweepstakes':
      return { one: 'sweepstake', many: 'sweepstakes' };
    case 'terms_of_service':
      return { one: 'ToS version', many: 'ToS versions' };
    case 'questionnaire':
      return { one: 'questionnaire', many: 'questionnaires' };
    case 'task_reward_collection':
      return { one: 'collection', many: 'collections' };
    case 'plan_cohort_purse_mapping':
      return { one: 'mapping', many: 'mappings' };
    default:
      return { one: 'entity', many: 'entities' };
  }
}

/** Stringify changed fields as small outlined chips, "+N more" if overflow. */
const ChangedFieldChips: React.FC<{ fields: string[] }> = ({ fields }) => {
  const shown = fields.slice(0, 5);
  const extra = fields.length - shown.length;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      {shown.map((f) => (
        <Chip
          key={f}
          label={f}
          size="small"
          variant="outlined"
          sx={{
            fontSize: 11,
            fontFamily: 'Fira Code, monospace',
            color: colors.textSecondary,
            borderColor: colors.borderDefault,
            height: 22,
          }}
        />
      ))}
      {extra > 0 && (
        <Tooltip title={fields.slice(5).join(', ')}>
          <Chip
            label={`+${extra} more`}
            size="small"
            variant="outlined"
            sx={{
              fontSize: 11,
              color: colors.textTertiary,
              borderColor: colors.borderLight,
              height: 22,
            }}
          />
        </Tooltip>
      )}
      {shown.length === 0 && (
        <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>
          No field-level diff available
        </Typography>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Row: review state                                                  */
/* ------------------------------------------------------------------ */

const ReviewRow: React.FC<{ entity: PublishEntity; kind: PublishEntityKind }> = ({
  entity,
  kind,
}) => {
  const [diffOpen, setDiffOpen] = useState(false);
  const fromV = entity.baseline_version;
  const toV = entity.current_version;
  const versionLabel = fromV == null ? `new → v${toV}` : `v${fromV} → v${toV}`;
  const RowIcon = iconForKind(kind);
  const name = nameOf(entity);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderColor: colors.borderLight,
        borderRadius: '10px',
        backgroundColor: colors.bgWhite,
      }}
    >
      {/* Line 1 — icon + code + version chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <RowIcon sx={{ fontSize: 18, color: colors.adminAccent }} />
        <Box
          component="code"
          sx={{ ...typo.mono, fontSize: 13, fontWeight: 700, color: colors.textPrimary }}
        >
          {codeOf(entity)}
        </Box>
        <Chip
          label={versionLabel}
          size="small"
          variant="outlined"
          sx={{
            fontSize: 11,
            fontFamily: 'Fira Code, monospace',
            fontWeight: 700,
            color: colors.adminAccent,
            borderColor: colors.adminAccent,
            height: 22,
          }}
        />
        {name && (
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, flex: 1, minWidth: 0 }} noWrap>
            {name}
          </Typography>
        )}
      </Box>

      {/* Line 2 — changed fields */}
      <Box sx={{ mb: 1 }}>
        <ChangedFieldChips fields={entity.changed_fields ?? []} />
      </Box>

      {/* Line 3 — diff toggle */}
      {(entity.diff?.length ?? 0) > 0 && (
        <>
          <Button
            size="small"
            onClick={() => setDiffOpen((x) => !x)}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              minWidth: 0,
              p: 0.5,
              color: colors.adminAccent,
            }}
          >
            {diffOpen ? 'Hide diff' : 'View diff'}
          </Button>
          <Collapse in={diffOpen} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: colors.bgSubtle,
                borderRadius: '6px',
                border: `1px solid ${colors.borderLight}`,
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.textTertiary }}>
                      Field
                    </TableCell>
                    <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.errorText }}>
                      Old
                    </TableCell>
                    <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.successText }}>
                      New
                    </TableCell>
                  </TableRow>
                  {entity.diff!.map((d) => (
                    <TableRow key={d.field}>
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: colors.textSecondary,
                        }}
                      >
                        {d.field}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: colors.errorText,
                          backgroundColor: colors.errorBg,
                        }}
                      >
                        {renderVal(d.old)}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: colors.successText,
                          backgroundColor: colors.successBg,
                        }}
                      >
                        {renderVal(d.new)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

/* ------------------------------------------------------------------ */
/*  Row: publishing/result state                                       */
/* ------------------------------------------------------------------ */

const StatusRow: React.FC<{
  entity: PublishEntity;
  runtime: RowRuntime;
  onRetry?: () => void;
}> = ({ entity, runtime, onRetry }) => {
  let icon: React.ReactNode;
  let label: string;
  let color: string = colors.textSecondary;

  switch (runtime.status) {
    case 'queued':
      icon = <HourglassEmptyIcon sx={{ fontSize: 16, color: colors.textTertiary }} />;
      label = 'Queued';
      color = colors.textTertiary;
      break;
    case 'publishing':
      icon = <CircularProgress size={14} sx={{ color: colors.adminAccent }} />;
      label = 'Publishing…';
      color = colors.adminAccent;
      break;
    case 'published':
      icon = <CheckCircleIcon sx={{ fontSize: 16, color: colors.successIcon }} />;
      label = `Published v${runtime.published_version ?? entity.current_version}`;
      color = colors.successText;
      break;
    case 'failed':
      icon = <ErrorIcon sx={{ fontSize: 16, color: colors.errorIcon }} />;
      label = runtime.message || `Failed${runtime.error ? ` (${runtime.error})` : ''}`;
      color = colors.errorText;
      break;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        borderColor: runtime.status === 'failed' ? colors.errorBorder : colors.borderLight,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: runtime.status === 'failed' ? colors.errorBg : colors.bgWhite,
      }}
    >
      {icon}
      <Box
        component="code"
        sx={{ ...typo.mono, fontSize: 12, fontWeight: 700, color: colors.textPrimary }}
      >
        {codeOf(entity)}
      </Box>
      <Typography sx={{ fontSize: 12, color, flex: 1, minWidth: 0 }} noWrap>
        {label}
      </Typography>
      {runtime.status === 'failed' && onRetry && (
        <WriteButton
          size="small"
          onClick={onRetry}
          sx={{ textTransform: 'none', fontSize: 12, color: colors.errorText }}
        >
          Retry
        </WriteButton>
      )}
    </Paper>
  );
};

/* ------------------------------------------------------------------ */
/*  Main dialog                                                        */
/* ------------------------------------------------------------------ */

const PublishConfirmationDialog: React.FC<PublishConfirmationDialogProps> = ({
  open,
  onClose,
  entity,
  entities,
  onCompleted,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [phase, setPhase] = useState<DialogPhase>('review');
  const [runtime, setRuntime] = useState<Record<string, RowRuntime>>({});
  const [batchId, setBatchId] = useState<string | null>(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const [shake, setShake] = useState(false);

  // Ref copy to avoid stale closures inside async publish loop.
  const phaseRef = useRef<DialogPhase>('review');
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Mint a batch id once per dialog-open so retries on failed rows stay
  // grouped under the same server-side history batch. Memoized on `open`
  // so reopening the dialog mints fresh.
  const openBatchId = useMemo(() => (open ? mintBatchId() : null), [open]);

  // Resolve the dominant entity kind — explicit prop wins, else first
  // entity's kind, else 'task'. Mixed-kind batches aren't supported in
  // R4 (the dialog dispatches a single publish call); keep the first
  // kind and rely on the caller to segregate lists.
  const resolvedKind: PublishEntityKind = useMemo(() => {
    if (entity) return entity;
    if (entities[0]) return kindOf(undefined, entities[0]);
    return 'task';
  }, [entity, entities]);

  // Reset when (re)opened so the caller can reuse this dialog for
  // consecutive publishes without it remembering stale state.
  useEffect(() => {
    if (open) {
      setPhase('review');
      setRuntime(
        Object.fromEntries(entities.map((e) => [codeOf(e), { status: 'queued' as const }])),
      );
      setBatchId(openBatchId);
      setProgressIndex(0);
    }
  }, [open, entities, openBatchId]);

  const total = entities.length;
  const failedRows = useMemo(
    () => entities.filter((e) => runtime[codeOf(e)]?.status === 'failed'),
    [entities, runtime],
  );
  const successRows = useMemo(
    () => entities.filter((e) => runtime[codeOf(e)]?.status === 'published'),
    [entities, runtime],
  );

  /* -------------------------- Publish driver --------------------------- */

  const runPublish = useCallback(
    async (subset: PublishEntity[]) => {
      setPhase('publishing');
      setProgressIndex(0);

      // Reset statuses for the subset (retry loop reuses this path).
      setRuntime((prev) => {
        const next = { ...prev };
        subset.forEach((e) => {
          next[codeOf(e)] = { status: 'queued' };
        });
        return next;
      });

      for (let i = 0; i < subset.length; i++) {
        const code = codeOf(subset[i]);
        setRuntime((prev) => ({ ...prev, [code]: { status: 'publishing' } }));
        setProgressIndex(i);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 120));
      }

      const bid = openBatchId ?? mintBatchId();

      try {
        const response = await dispatchPublish(resolvedKind, subset, bid);

        const byCode = new Map(response.results.map((r) => [r.code, r]));

        setRuntime((prev) => {
          const next = { ...prev };
          subset.forEach((e) => {
            const code = codeOf(e);
            const r = byCode.get(code);
            if (!r) {
              next[code] = {
                status: 'failed',
                error: 'no_result',
                message: 'Server returned no result for this entity.',
              };
            } else if (r.ok) {
              next[code] = {
                status: 'published',
                published_version: r.published_version,
              };
            } else {
              next[code] = {
                status: 'failed',
                error: r.error,
                message:
                  r.message ||
                  (r.error === 'version_conflict'
                    ? `Someone else saved v${r.current_version}. Reload.`
                    : `Failed: ${r.error ?? 'unknown'}`),
              };
            }
          });
          return next;
        });
        setBatchId(response.batch_id || bid);
        setProgressIndex(subset.length);
        setPhase('result');
      } catch (err: any) {
        setRuntime((prev) => {
          const next = { ...prev };
          subset.forEach((e) => {
            next[codeOf(e)] = {
              status: 'failed',
              error: err?.response?.data?.error ?? 'network',
              message: 'Publish request failed — check connection and retry.',
            };
          });
          return next;
        });
        setProgressIndex(subset.length);
        setPhase('result');
      }
    },
    [resolvedKind, openBatchId],
  );

  /* -------------------------- Handlers --------------------------- */

  const handleClose = useCallback(
    (_: unknown, reason?: string) => {
      if (phase === 'publishing') {
        setShake(true);
        setTimeout(() => setShake(false), 400);
        return;
      }
      if (reason === 'backdropClick' && phase === 'review') {
        // Reviewing is safe to back out of.
      }
      onCompleted(phase === 'result' ? { batch_id: batchId ?? '', results: [] } : null);
      onClose();
    },
    [phase, onClose, onCompleted, batchId],
  );

  const handlePublish = () => {
    if (phase === 'review') {
      runPublish(entities);
    } else if (phase === 'result' && failedRows.length > 0) {
      runPublish(failedRows);
    } else {
      handleClose(null);
    }
  };

  /* -------------------------- Keyboard --------------------------- */

  useEffect(() => {
    if (!open) return;
    const onKey = (ev: KeyboardEvent) => {
      const cmd = ev.metaKey || ev.ctrlKey;
      if (cmd && ev.key === 'Enter') {
        ev.preventDefault();
        handlePublish();
      } else if (ev.key === 'Escape') {
        if (phaseRef.current === 'publishing') {
          ev.preventDefault();
          setShake(true);
          setTimeout(() => setShake(false), 400);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entities, failedRows.length]);

  /* -------------------------- Render --------------------------- */

  const { one: entityNoun, many: entityNounPlural } = nounForKind(resolvedKind);
  const entityNounFor = (n: number) => (n === 1 ? entityNoun : entityNounPlural);

  const headerTitle = (() => {
    if (phase === 'review') {
      if (resolvedKind === 'adventure') return 'Publish adventures to platform';
      if (resolvedKind === 'cohort') return 'Publish cohorts to platform';
      if (resolvedKind === 'sweepstakes') return 'Publish sweepstakes to platform';
      if (resolvedKind === 'terms_of_service') return 'Publish ToS versions to platform';
      if (resolvedKind === 'questionnaire') return 'Publish questionnaires to platform';
      if (resolvedKind === 'task_reward_collection')
        return 'Publish collections to platform';
      if (resolvedKind === 'plan_cohort_purse_mapping')
        return 'Publish mappings to platform';
      return 'Publish to platform';
    }
    if (phase === 'publishing') {
      return `Publishing… ${Math.min(progressIndex + 1, total)} of ${total}`;
    }
    if (failedRows.length === 0) {
      return `Published ${successRows.length} of ${total} ${entityNounFor(total)}`;
    }
    return `Published ${successRows.length} of ${total} — ${failedRows.length} failed`;
  })();

  const primaryLabel = (() => {
    if (phase === 'review') return `Publish ${total} ${entityNounFor(total)}`;
    if (phase === 'publishing') return 'Publishing…';
    if (failedRows.length > 0) return `Retry ${failedRows.length} failed`;
    return 'Close';
  })();

  const progressPct = total === 0 ? 0 : Math.min(100, (progressIndex / total) * 100);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableEscapeKeyDown={phase === 'publishing'}
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', md: 900 },
          animation: shake ? 'shake 0.4s' : undefined,
          '@keyframes shake': {
            '0%,100%': { transform: 'translateX(0)' },
            '25%': { transform: 'translateX(-6px)' },
            '75%': { transform: 'translateX(6px)' },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: colors.bgWhite,
          borderBottom: `1px solid ${colors.borderLight}`,
          pr: 6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ ...typo.sectionTitle, fontSize: 18 }}>{headerTitle}</Typography>
            {phase === 'review' && (
              <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.25 }}>
                Review {total} {entityNounFor(total)} before pushing changes to the platform.
              </Typography>
            )}
          </Box>
          {phase !== 'publishing' && (
            <IconButton aria-label="Close" onClick={() => handleClose(null)} size="small" sx={{ ml: 2 }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        {phase === 'publishing' && (
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              mt: 1.5,
              height: 6,
              borderRadius: 3,
              backgroundColor: colors.adminAccentSubtle,
              '& .MuiLinearProgress-bar': { backgroundColor: colors.adminAccent },
            }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: colors.bgSubtle }}>
        {phase === 'review' && (
          <>
            <Stack>
              {entities.map((e) => (
                <ReviewRow key={codeOf(e)} entity={e} kind={resolvedKind} />
              ))}
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                color: colors.textPrimary,
                fontWeight: 700,
                mt: 2,
                textAlign: 'center',
              }}
            >
              Publishing {total} {entityNounFor(total)}.
            </Typography>
          </>
        )}

        {phase === 'publishing' && (
          <Box role="status" aria-live="polite">
            {entities.map((e) => (
              <StatusRow
                key={codeOf(e)}
                entity={e}
                runtime={runtime[codeOf(e)] ?? { status: 'queued' }}
              />
            ))}
          </Box>
        )}

        {phase === 'result' && (
          <>
            <Alert
              severity={failedRows.length === 0 ? 'success' : 'warning'}
              sx={{ mb: 2, borderRadius: '8px' }}
              action={
                <Tooltip title="In-history navigation lands in Round 5">
                  <span>
                    <Button size="small" disabled>
                      View in history
                    </Button>
                  </span>
                </Tooltip>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>
                  {failedRows.length === 0
                    ? `All ${successRows.length} published.`
                    : `${successRows.length} published, ${failedRows.length} failed.`}
                </span>
                {batchId && (
                  <Box
                    component="code"
                    sx={{
                      ...typo.mono,
                      fontSize: 11,
                      color: colors.textSecondary,
                      backgroundColor: colors.bgMuted,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                    }}
                  >
                    batch_id: {batchId}
                  </Box>
                )}
              </Box>
            </Alert>
            <Box role="status" aria-live="polite">
              {entities.map((e) => {
                const rt = runtime[codeOf(e)] ?? { status: 'queued' as const };
                return (
                  <StatusRow
                    key={codeOf(e)}
                    entity={e}
                    runtime={rt}
                    onRetry={rt.status === 'failed' ? () => runPublish([e]) : undefined}
                  />
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
        }}
      >
        {phase !== 'publishing' && (
          <Button onClick={() => handleClose(null)} sx={secondaryButtonSx}>
            {phase === 'result' && failedRows.length === 0 ? 'Close' : 'Cancel'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <WriteButton
          variant="contained"
          disabled={phase === 'publishing' || total === 0}
          onClick={handlePublish}
          sx={{
            ...primaryButtonSx,
            backgroundColor: colors.adminAccent,
            color: colors.bgWhite,
            '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            '&.Mui-disabled': { backgroundColor: colors.adminAccentSubtle },
          }}
        >
          {primaryLabel}
          <Box component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5 }}>
            (⌘↵)
          </Box>
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
};

export default PublishConfirmationDialog;

/* ------------------------------------------------------------------ */
/*  Adapters                                                           */
/* ------------------------------------------------------------------ */

/**
 * Task-row → PublishEntity adapter. Used by TasksList bulk + TaskEdit
 * single-publish. Keeps `task_code` populated for any legacy caller
 * that reads it directly off the returned object.
 */
export function toPublishEntity(row: AdminTaskRow): PublishEntity {
  return {
    entity: 'task',
    code: row.task_code,
    task_code: row.task_code,
    task_name: row.task_name,
    name: row.task_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Adventure-row → PublishEntity adapter. */
export function toPublishEntityFromAdventure(row: AdminAdventureRow): PublishEntity {
  return {
    entity: 'adventure',
    code: row.adventure_code,
    name: row.adventure_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Cohort-row → PublishEntity adapter. */
export function toPublishEntityFromCohort(row: AdminCohortRow): PublishEntity {
  return {
    entity: 'cohort',
    code: row.cohort_code,
    name: row.cohort_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Sweepstakes-row → PublishEntity adapter. */
export function toPublishEntityFromSweepstakes(row: AdminSweepstakesRow): PublishEntity {
  return {
    entity: 'sweepstakes',
    code: row.sweepstakes_code,
    name: row.sweepstakes_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** ToS-row → PublishEntity adapter. */
export function toPublishEntityFromToS(row: AdminToSRow): PublishEntity {
  return {
    entity: 'terms_of_service',
    code: row.tos_code,
    name: row.tos_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Questionnaire-row → PublishEntity adapter. */
export function toPublishEntityFromQuestionnaire(
  row: AdminQuestionnaireRow,
): PublishEntity {
  return {
    entity: 'questionnaire',
    code: row.questionnaire_code,
    name: row.questionnaire_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Collection-row → PublishEntity adapter. */
export function toPublishEntityFromCollection(
  row: AdminCollectionRow,
): PublishEntity {
  return {
    entity: 'task_reward_collection',
    code: row.collection_code,
    name: row.collection_name,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}

/** Plan-cohort-purse mapping row → PublishEntity adapter. */
export function toPublishEntityFromMapping(
  row: AdminPlanCohortPurseMappingRow,
): PublishEntity {
  return {
    entity: 'plan_cohort_purse_mapping',
    code: row.mapping_code,
    name: `${row.plan_code} · ${row.cohort_code} → ${row.purse_code}`,
    current_version: row.current_version,
    baseline_version:
      row.status === 'never_published'
        ? null
        : row.status === 'unpublished'
          ? Math.max(1, row.current_version - 1)
          : row.current_version,
    changed_fields: [],
    diff: [],
  };
}
